import { create } from 'zustand';
import type { CollectionActivityInput } from '../services/api/collections.service';

/** A collection follow-up written without a connection, waiting to be sent. */
export interface QueuedFollowUp {
  /** Also the reference the server records it under, so a resend is not counted twice. */
  id: string;
  companyId: number;
  partyCode: string;
  customerName: string;
  activity: CollectionActivityInput;
  recordedAt: string;
  /** Set when the server turned it down; it stays until the user removes it. */
  error?: string;
}

interface OutboxState {
  /** Whose device queue is loaded: entries are kept per person. */
  identity: string | null;
  items: QueuedFollowUp[];
  load: (identity: string | null) => void;
  add: (item: QueuedFollowUp) => void;
  remove: (id: string) => void;
  refuse: (id: string, error: string) => void;
  /** Forgets every person's queue on this device (signing out). */
  clearAll: () => void;
}

const PREFIX = 'fh:outbox:';

function read(identity: string): QueuedFollowUp[] {
  try {
    const items = JSON.parse(window.localStorage.getItem(PREFIX + identity) ?? '[]');
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
}

function write(identity: string | null, items: QueuedFollowUp[]) {
  if (!identity) return;
  try {
    if (items.length) window.localStorage.setItem(PREFIX + identity, JSON.stringify(items));
    else window.localStorage.removeItem(PREFIX + identity);
  } catch {
    // The entry stays in memory for this visit.
  }
}

export const useOutbox = create<OutboxState>()((set, get) => {
  const update = (items: QueuedFollowUp[]) => {
    write(get().identity, items);
    set({ items });
  };
  return {
    identity: null,
    items: [],
    load: (identity) => {
      if (identity === get().identity) return;
      set({ identity, items: identity ? read(identity) : [] });
    },
    add: (item) => update([...get().items, item]),
    remove: (id) => update(get().items.filter((item) => item.id !== id)),
    refuse: (id, error) => update(get().items.map((item) => (item.id === id ? { ...item, error } : item))),
    clearAll: () => {
      try {
        Object.keys(window.localStorage)
          .filter((key) => key.startsWith(PREFIX))
          .forEach((key) => window.localStorage.removeItem(key));
      } catch {
        // Nothing stored.
      }
      set({ items: [] });
    },
  };
});

/** A reference unique enough to tell entries apart; works on plain-http test hosts too. */
export function newEntryId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
